import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export type MenuPlacement = 'right' | 'below';

/**
 * A pop-up menu anchored to a button, rendered into the surrounding `.pane` (so no scrolling or
 * overflow-hidden ancestor clips it) and kept inside the pane's bounds: it is shifted back when
 * it would run off an edge and scrolls internally when taller than the pane.
 */
export function PaneMenu({
  anchor,
  placement,
  onRequestClose,
  className,
  children,
}: {
  anchor: HTMLElement;
  placement: MenuPlacement;
  onRequestClose: () => void;
  className?: string;
  children: ReactNode;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const host = anchor.closest<HTMLElement>('.pane') ?? document.body;
  const [style, setStyle] = useState<CSSProperties>({ visibility: 'hidden' });

  useLayoutEffect(() => {
    const place = () => {
      const el = ref.current;
      if (!el) return;
      const pad = 8;
      const hr = host.getBoundingClientRect();
      const ar = anchor.getBoundingClientRect();
      const maxH = Math.max(80, hr.height - pad * 2);
      el.style.maxHeight = `${maxH}px`;
      const mw = el.offsetWidth;
      const mh = Math.min(el.offsetHeight, maxH);
      // On phones the toolbar is a bar along the bottom edge (see the 720px media query), so a
      // menu that would open sideways opens upwards instead.
      const bottomBar = placement === 'right' && window.matchMedia('(max-width: 720px)').matches;
      let left = bottomBar ? ar.left - hr.left : placement === 'right' ? ar.right - hr.left + 10 : ar.left - hr.left;
      let top = bottomBar ? ar.top - hr.top - mh - 10 : placement === 'right' ? ar.top - hr.top : ar.bottom - hr.top + 6;
      if (left + mw > hr.width - pad) left = placement === 'right' && !bottomBar ? ar.left - hr.left - mw - 10 : hr.width - pad - mw;
      if (top + mh > hr.height - pad) top = hr.height - pad - mh;
      left = Math.max(pad, left);
      top = Math.max(pad, top);
      setStyle({ left, top, maxHeight: maxH, visibility: 'visible' });
    };
    place();
    const ro = new ResizeObserver(place);
    ro.observe(host);
    return () => ro.disconnect();
  }, [anchor, host, placement]);

  useLayoutEffect(() => {
    const close = (e: PointerEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !anchor.contains(t)) onRequestClose();
    };
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onRequestClose();
    };
    window.addEventListener('pointerdown', close, true);
    window.addEventListener('keydown', key, true);
    return () => {
      window.removeEventListener('pointerdown', close, true);
      window.removeEventListener('keydown', key, true);
    };
  }, [anchor, onRequestClose]);

  return createPortal(
    <div
      ref={ref}
      className={`menu pane-menu${className ? ` ${className}` : ''}`}
      role="menu"
      style={style}
      onPointerDown={(e) => e.stopPropagation()}
    >
      {children}
    </div>,
    host,
  );
}
